import type {
  MetricsBlock,
  RegionRow,
  TopJob,
  UnattributedBooks,
  VelocityPair,
} from '../../api/types.js';
import { accentFor, color, font, fontSize, radius, seriesColor } from '../../theme/tokens.js';
import { DataQualityNote } from '../DataQualityBadge/DataQualityBadge.js';
import { MetricCell, formatValue, formatValueExact } from '../MetricCell/MetricCell.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { BreakdownPanel, ProvenanceFootnote, breakdownRows } from '../Breakdown/Breakdown.js';
import { provenanceFor } from '../Breakdown/provenance.js';
import type { CardSortState } from '../../state/filters.js';

/**
 * The Cards view (FR-031, PRD §6.2).
 *
 * The same data as the table, laid out as the blueprint's per-region card. Activating a card
 * reaches Region Detail, exactly as a table row or a pill does — three routes to the same
 * place, which is what FR-030 and FR-031 ask for.
 *
 * Every numeric figure still goes through `<MetricCell>`, so the trend arrow, the `—`
 * fallback, and the keyboard-reachable tooltip behave identically here and in the table. A
 * bespoke card layout with its own number rendering is exactly how those rules drift apart.
 */

export interface RegionCardsProps {
  rows: RegionRow[];
  combo: string;
  onSelectRegion: (regionCode: string) => void;
  unattributedBooks: UnattributedBooks | null | undefined;
  /**
   * Which figure orders the cards, and which way (blueprint § card sort).
   *
   * The cards have no column headers to click, so the ordering has to be its own control —
   * which is why this arrives as state from the page rather than living in a `<DataTable>` the
   * way the table's sort does. Omitted, the cards stay in `UI_REGIONS` order.
   */
  sort?: CardSortState;
}

/**
 * One metric tile: the **total**, its label, and the **Nursing · Allied split beneath it**.
 *
 * The split is the point of the tile — it is what the region table cannot show without
 * switching the specialty filter and losing the total. `nursing` and `allied` come from the
 * same metric read out of the `…|nursing` and `…|allied` combos, so the three numbers are one
 * figure decomposed rather than three separate computations that could disagree.
 */
function Tile({
  label,
  fullLabel,
  metricKey,
  metric,
  nursing,
  allied,
  background,
  valueColor,
  labelColor,
}: {
  label: string;
  /** Spelled out for the panel and the announced name — the face carries the short code. */
  fullLabel: string;
  metricKey: string;
  metric: MetricsBlock[string];
  nursing: MetricsBlock[string];
  allied: MetricsBlock[string];
  background: string;
  valueColor: string;
  labelColor?: string;
}) {
  return (
    <div style={{ background, borderRadius: radius['2xl'], padding: '9px 10px' }}>
      {/*
        The figure opens the same panel a table cell does (owner request 2026-09-10).

        It carried no disclosure at all until then, which made these five the only prominent
        figures on this view a reader could not ask a question of — and the Cards view is where
        a reader who does not already know the columns starts. The rows are the tile's own
        `N · A` subtext stated exactly, so the panel adds the digits and the lineage rather than
        a second decomposition the face does not already show.
      */}
      <Disclosure
        accessibleName={`${fullLabel}: ${formatValueExact(metric?.value ?? null)}`}
        panel={
          <BreakdownPanel
            title={fullLabel}
            total={formatValueExact(metric?.value ?? null)}
            rows={breakdownRows({
              total: metric?.value ?? null,
              nursing: nursing?.value ?? null,
              allied: allied?.value ?? null,
              // The book columns take the specialty axis alone (§9.8.2); an Internal or Target
              // row here would restate the total under another name.
              includeSource: false,
              includeTargeting: false,
            })}
            provenance={provenanceFor(metricKey)}
          />
        }
        style={{ padding: 0, background: 'none', border: 'none', cursor: 'help' }}
      >
        <span
          className="mono"
          style={{
            fontFamily: font.mono,
            fontSize: fontSize.statValue,
            fontWeight: 600,
            color: valueColor,
          }}
        >
          {formatValue(metric?.value ?? null)}
        </span>
      </Disclosure>
      <div
        style={{
          fontSize: fontSize.label,
          color: labelColor ?? color.inkMuted,
          fontWeight: 500,
          marginTop: 1,
        }}
      >
        {label}
      </div>
      <div
        className="mono"
        style={{
          fontFamily: font.mono,
          fontSize: fontSize.micro,
          color: color.inkSubtle,
          marginTop: 4,
        }}
      >
        {formatValue(nursing?.value ?? null)} N{' '}
        <span style={{ color: color.borderStrong }}>·</span>{' '}
        {formatValue(allied?.value ?? null)} A
      </div>
    </div>
  );
}

/**
 * One footer figure, stated as its two windows: `17 / 51`.
 *
 * A pair rather than two tiles because the reader is comparing them — a month against a quarter
 * — and splitting them apart turns one comparison into two lookups. `—` for a window that could
 * not be computed, on either side independently (FR-055).
 */
function VelocityStat({
  label,
  metricKey,
  pair,
  suffix = '',
  valueColor,
}: {
  label: string;
  /**
   * The underlying metric, for the panel's lineage footer — `jobCount`, `covPct` and the rest.
   *
   * ⚠ The footer states how the **figure** is counted and deliberately does not restate the
   * window: this pair is a fixed 30 and 90 days, the label above it already says so, and a
   * sentence repeating it would be a second place for that fact to go stale.
   */
  metricKey: string;
  pair: VelocityPair | undefined;
  suffix?: string;
  valueColor?: string;
}) {
  const show = (v: number | null | undefined) =>
    v == null ? '—' : `${formatValue(v)}${suffix}`;

  return (
    <div>
      <div
        style={{
          fontSize: fontSize.micro,
          fontWeight: 600,
          letterSpacing: '.4px',
          color: color.inkFaint,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <Disclosure
        accessibleName={`${label}: ${show(pair?.last30)} in the last 30 days, ${show(
          pair?.last90,
        )} in the last 90 days`}
        panel={
          <span>
            <strong>{label}</strong>
            <span style={{ display: 'block' }}>Last 30 days: {show(pair?.last30)}</span>
            <span style={{ display: 'block' }}>Last 90 days: {show(pair?.last90)}</span>
            <ProvenanceFootnote provenance={provenanceFor(metricKey)} />
          </span>
        }
      >
        <span
          className="mono"
          style={{
            fontFamily: font.mono,
            fontSize: '14px',
            fontWeight: 600,
            color: valueColor ?? color.inkBody,
            marginTop: 2,
            whiteSpace: 'nowrap',
          }}
        >
          {show(pair?.last30)} <span style={{ color: color.borderStrong }}>/</span>{' '}
          {show(pair?.last90)}
        </span>
      </Disclosure>
    </div>
  );
}

/**
 * `RN (9), LPN/LVN (8), …` — one region's job demand for one offering over the last 90 days.
 *
 * The count in parentheses is **openings**, not jobs: one requisition for six nurses is six
 * openings and one job, and demand is the former (PRD §9.4). An empty list says so in words
 * rather than rendering an empty row, because a bare label with nothing after it reads as a
 * loading state that never finished.
 */
function TopJobsLine({
  label,
  jobs,
  accent,
}: {
  label: string;
  jobs: TopJob[];
  accent: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
      <div
        style={{
          fontSize: fontSize.micro,
          fontWeight: 600,
          letterSpacing: '.4px',
          color: color.inkFaint,
          textTransform: 'uppercase',
          flex: 'none',
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          fontSize: fontSize.body,
        }}
      >
        {jobs.length === 0 ? (
          <span style={{ color: color.inkFaint }}>No openings in the last 90 days</span>
        ) : (
          jobs.map((job, index) => (
            <span key={job.label} style={{ color: color.inkBody }}>
              {job.label}{' '}
              <span
                className="mono"
                style={{ fontFamily: font.mono, color: accent, fontWeight: 600 }}
              >
                ({formatValue(job.openings)})
              </span>
              {index < jobs.length - 1 ? (
                <span style={{ color: color.borderStrong }}>,</span>
              ) : null}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

/** `+174 Nursing  +62 Allied` for one window, or a muted `—` where the refresh recorded none. */
function AddsLine({
  window,
  adds,
}: {
  window: '30d' | '90d';
  adds: { nursing: number | null; allied: number | null } | null | undefined;
}) {
  const show = (v: number | null | undefined) => (v == null ? '—' : `+${formatValue(v)}`);
  return (
    <div>
      <span style={{ color: color.inkFaint, fontWeight: 600 }}>{window}</span>{' '}
      <span style={{ color: color.positive, fontWeight: 600 }}>{show(adds?.nursing)}</span>
      <span style={{ color: color.inkSubtle }}> Nursing</span>{' '}
      <span style={{ color: color.positive, fontWeight: 600 }}>{show(adds?.allied)}</span>
      <span style={{ color: color.inkSubtle }}> Allied</span>
    </div>
  );
}

/**
 * The cards in the order the sort control asks for.
 *
 * Sorted over `metricsByCombo` — the same numbers on screen under the same filters — so a card
 * ranked first is visibly first by the figure the control names. A region whose value is `null`
 * sinks to the bottom in **either** direction: "we could not compute it" is not a small number,
 * and letting it lead an ascending sort would put the least informative card first.
 */
function sortRows(
  rows: RegionRow[],
  combo: string,
  sort: CardSortState | undefined,
): RegionRow[] {
  if (!sort) return rows;
  const valueOf = (row: RegionRow) =>
    (row.metricsByCombo?.[combo] ?? row.metrics ?? {})[sort.key]?.value ?? null;

  return [...rows].sort((a, b) => {
    const av = valueOf(a);
    const bv = valueOf(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    // Region code breaks a tie, so equal figures cannot reshuffle between renders.
    if (av === bv) return a.regionCode.localeCompare(b.regionCode);
    return sort.direction === 'desc' ? bv - av : av - bv;
  });
}

export function RegionCards({
  rows,
  combo,
  onSelectRegion,
  unattributedBooks,
  sort,
}: RegionCardsProps) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 18 }}>
      {sortRows(rows, combo, sort).map((region) => {
        const metrics = region.metricsByCombo?.[combo] ?? region.metrics ?? {};
        const velocity = region.velocityByCombo?.[combo] ?? {};
        // The same source/targeting the card is showing, re-read at each specialty. Swapping
        // only the last segment keeps the split honest: `N + A` decomposes the total above it
        // rather than being a differently-filtered pair of numbers.
        const [source, targeting] = combo.split('|');
        const nursing = region.metricsByCombo?.[`${source}|${targeting}|nursing`] ?? {};
        const allied = region.metricsByCombo?.[`${source}|${targeting}|allied`] ?? {};
        const accent = accentFor(region.regionCode);

        return (
          <div
            key={region.regionCode}
            className="rcard"
            role="button"
            tabIndex={0}
            onClick={() => onSelectRegion(region.regionCode)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectRegion(region.regionCode);
              }
            }}
            aria-label={`${region.regionName} (${region.regionCode}) — open region detail`}
            style={{
              background: color.surface,
              border: `1px solid ${color.border}`,
              borderLeft: `4px solid ${accent}`,
              borderRadius: radius['6xl'],
              cursor: 'pointer',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '16px 20px 14px',
                borderBottom: `1px solid ${color.borderSubtle}`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  className="mono"
                  style={{
                    fontFamily: font.mono,
                    fontSize: fontSize.caption,
                    fontWeight: 600,
                    color: color.onAccent,
                    background: accent,
                    padding: '2px 8px',
                    borderRadius: radius.md,
                  }}
                >
                  {region.regionCode}
                </span>
                <span
                  style={{
                    fontSize: fontSize.cardTitle,
                    fontWeight: 700,
                    letterSpacing: '-.3px',
                    color: color.ink,
                  }}
                >
                  {region.regionName}
                </span>
                {region.isSpecial ? (
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '.4px',
                      color: color.warning,
                      background: color.noticeBg,
                      border: '1px solid #f6dcae',
                      padding: '2px 7px',
                      borderRadius: radius.sm,
                      textTransform: 'uppercase',
                    }}
                  >
                    Special Project
                  </span>
                ) : null}
              </div>

              {/* Who runs the region. The table carries only LDR, a count — on a card there is
                    room for the name, and it is the first thing a reader wants beside the code.
                    Two leaders join with `&`, matching the blueprint; none renders `—` rather
                    than an empty line, so the absence is stated rather than looking unfinished. */}
              <div
                style={{
                  fontSize: fontSize.captionStrong,
                  color: color.inkSubtle,
                  marginTop: 4,
                }}
              >
                {region.leaders.length === 1 ? 'Leader' : 'Leaders'} ·{' '}
                {region.leaders.length > 0 ? region.leaders.join(' & ') : '—'}
              </div>
            </div>

            <div
              style={{
                padding: '13px 20px 4px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 7,
                fontSize: fontSize.body,
                color: color.inkBody,
                alignItems: 'center',
              }}
            >
              {/*
                Headcounts, never trended (`flatMetric`), so no trend slot: it is 36px since
                2026-09-27 and would open a gap before every word in this sentence.
              */}
              <MetricCell
                metric={metrics.recruiters}
                metricKey="recruiters"
                label="Recruiters"
                align="left"
                showTrendArrow={false}
                bold
              />
              <span>recruiters</span>
              <span style={{ color: color.borderStrong }}>·</span>
              <MetricCell
                metric={metrics.managerCount}
                metricKey="managerCount"
                label="Managers"
                align="left"
                showTrendArrow={false}
                bold
              />
              <span>managers</span>
              <span style={{ color: color.borderStrong }}>·</span>
              <MetricCell
                metric={metrics.recruitersNursing}
                metricKey="recruitersNursing"
                label="Recruiters — Nursing"
                align="left"
                showTrendArrow={false}
                colorOverride={color.nursing}
              />
              <span style={{ color: color.nursing }}>Nursing</span>
              <span style={{ color: color.borderStrong }}>·</span>
              <MetricCell
                metric={metrics.recruitersAllied}
                metricKey="recruitersAllied"
                label="Recruiters — Allied"
                align="left"
                showTrendArrow={false}
                colorOverride={color.allied}
              />
              <span style={{ color: color.allied }}>Allied</span>
              <span style={{ color: color.borderStrong }}>·</span>
              <MetricCell
                metric={metrics.targetAccounts}
                metricKey="targetAccounts"
                label="Target Accounts"
                align="left"
                showTrendArrow={false}
              />
              <span>target accts</span>
            </div>

            <div
              style={{ padding: '12px 20px 14px', display: 'flex', gap: 18, flexWrap: 'wrap' }}
            >
              <div style={{ flex: 'none' }}>
                <div
                  style={{
                    fontSize: fontSize.label,
                    fontWeight: 600,
                    letterSpacing: '.5px',
                    color: color.inkSubtle,
                    textTransform: 'uppercase',
                  }}
                >
                  Book of Business
                </div>
                <div
                  className="mono"
                  style={{
                    fontFamily: font.mono,
                    fontSize: fontSize.bobValue,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: color.ink,
                    marginTop: 4,
                  }}
                >
                  {formatValue(metrics.bob?.value ?? null)}
                </div>

                {/* What the book gained, by discipline. Two fixed windows, deliberately NOT
                    the period control: the BOB figure above answers to no period (§9.8.2), so
                    a period-scoped adds line would compare a month of arrivals against an
                    all-time book and read as its decomposition. The windows are named on the
                    line for that reason. Counts candidate records created in the window, so a
                    candidate moved between books is not an add anywhere (§9.8.5). */}
                <div
                  className="mono"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    marginTop: 6,
                    fontFamily: font.mono,
                    fontSize: fontSize.caption,
                  }}
                >
                  <AddsLine window="30d" adds={region.bobAdds?.d30} />
                  <AddsLine window="90d" adds={region.bobAdds?.d90} />
                </div>

                <DataQualityNote unattributedBooks={unattributedBooks} figureLabel="BoB" />
              </div>

              <div
                style={{
                  flex: 1,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, minmax(90px, 1fr))',
                  gap: 9,
                }}
              >
                <Tile
                  label="BOB"
                  fullLabel="Total Candidates in BoB"
                  metricKey="bob"
                  metric={metrics.bob}
                  nursing={nursing.bob}
                  allied={allied.bob}
                  background={color.tileBobBg}
                  valueColor={color.tileBobInk}
                />
                <Tile
                  label="TOA"
                  fullLabel="Active TOA — Travelers on Assignment"
                  metricKey="activeToa"
                  metric={metrics.activeToa}
                  nursing={nursing.activeToa}
                  allied={allied.activeToa}
                  background={color.tileToaBg}
                  valueColor={seriesColor.toa}
                />
                <Tile
                  label="Pending"
                  fullLabel="Pending Candidates"
                  metricKey="pending"
                  metric={metrics.pending}
                  nursing={nursing.pending}
                  allied={allied.pending}
                  background={color.tilePendingBg}
                  valueColor={color.warning}
                  labelColor={color.tilePendingLabel}
                />
                <Tile
                  label="Prospect"
                  fullLabel="Prospect Candidates"
                  metricKey="prospect"
                  metric={metrics.prospect}
                  nursing={nursing.prospect}
                  allied={allied.prospect}
                  background={color.tileProspectBg}
                  valueColor={color.colProspect}
                  labelColor={color.tileProspectLabel}
                />
                <Tile
                  label="Prev. Employed"
                  fullLabel="Previously Employed"
                  metricKey="prevEmployed"
                  metric={metrics.prevEmployed}
                  nursing={nursing.prevEmployed}
                  allied={allied.prevEmployed}
                  background={color.tilePempBg}
                  valueColor={color.inkMuted}
                  labelColor={color.tilePempLabel}
                />
              </div>
            </div>

            {/* Velocity. Every figure here is a fixed 30/90 pair, including Coverage and
                Conversion — the blueprint shows those two as a single unlabelled percentage,
                and one number of unstated span beside five that name theirs is the ambiguity
                worth losing (owner decision, 2026-08-28).

                These are NOT the trend arrows: an arrow is a percent change between two
                windows, and these are the windows themselves. They narrow with Source /
                Targeting / Specialty like the tiles above, and stay put when the period moves. */}
            <div
              style={{
                padding: '12px 20px',
                background: color.surfaceSubtle,
                borderTop: `1px solid ${color.borderSubtle}`,
                display: 'flex',
                alignItems: 'center',
                gap: 18,
                flexWrap: 'wrap',
              }}
            >
              <VelocityStat label="Jobs 30/90" metricKey="jobCount" pair={velocity.jobCount} />
              <VelocityStat label="Subs 30/90" metricKey="subs" pair={velocity.subs} />
              <VelocityStat label="Coverage 30/90" metricKey="covPct" pair={velocity.covPct} suffix="%" />
              <VelocityStat label="Offers 30/90" metricKey="offers" pair={velocity.offers} />
              <VelocityStat label="Starts 30/90" metricKey="starts" pair={velocity.starts} />
              <VelocityStat
                label="Conversion 30/90"
                metricKey="convPct"
                pair={velocity.convPct}
                suffix="%"
                valueColor={color.nursing}
              />
            </div>

            {/* Where the demand is. Fixed 90 days and unfiltered — the two lines ARE the
                specialty split, so re-cutting them by the Specialty control would empty one. */}
            <div
              style={{
                padding: '12px 20px 14px',
                borderTop: `1px solid ${color.borderSubtle}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <TopJobsLine
                label="Top 5 Nursing Jobs · 90d"
                jobs={region.topJobs?.nursing ?? []}
                accent={color.nursing}
              />
              <TopJobsLine
                label="Top 5 Allied Jobs · 90d"
                jobs={region.topJobs?.allied ?? []}
                accent={color.allied}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
